/**
 * Products API Types
 * Zod schemas for API validation
 */

import { z } from '@spcc/core'

// ============================================================================
// Localized String Schema
// ============================================================================

export const localizedStringSchema = z.object({
  en: z.string(),
  ar: z.string().optional(),
})

// ============================================================================
// Category Schemas
// ============================================================================

const strapiLocalizationEntrySchema = z
  .object({
    locale: z.string(),
    name: z.string().optional(),
    description: z.string().nullable().optional(),
  })
  .passthrough()

export const categorySummaryDtoSchema = z.object({
  id: z.coerce.string(),
  documentId: z.string(),
  code: z.string().nullable(),
  name: z.string(),
  color: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  locale: z.string().optional(),
  localizations: z.array(strapiLocalizationEntrySchema).optional(),
})

export const categoryDtoSchema = categorySummaryDtoSchema.extend({
  description: z.string().nullable().optional(),
  parent: categorySummaryDtoSchema.nullable().optional(),
  skuCount: z.number().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type CategorySummaryDto = z.infer<typeof categorySummaryDtoSchema>
export type CategoryDto = z.infer<typeof categoryDtoSchema>

// ============================================================================
// SKU Schemas
// ============================================================================

export const skuSummaryDtoSchema = z.object({
  id: z.coerce.string(),
  documentId: z.string(),
  name: z.string(),
  isActive: z.boolean().default(true),
  variantCount: z.number().optional(),
  locale: z.string().optional(),
  localizations: z.array(strapiLocalizationEntrySchema).optional(),
})

export const skuDtoSchema = skuSummaryDtoSchema.extend({
  description: z.string().nullable().optional(),
  categories: z.array(categorySummaryDtoSchema).optional(),
  variants: z.array(z.any()).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type SKUSummaryDto = z.infer<typeof skuSummaryDtoSchema>
export type SKUDto = z.infer<typeof skuDtoSchema>

// ============================================================================
// Variant Schemas
// ============================================================================

export const packagingTypeSchema = z.enum(['bags', 'bulk'])
export const deliveryGroupingRuleSchema = z.enum(['allow_mixed', 'require_same'])
export const portalAvailabilitySchema = z.enum(['ecommerce', 'portal', 'both'])

export const variantSummaryDtoSchema = z.object({
  id: z.coerce.string(),
  documentId: z.string(),
  name: z.string(),
  variantCode: z.string(),
  barcode: z.string(),
  weight: z.number(),
  packaging: packagingTypeSchema,
  portalAvailability: portalAvailabilitySchema.default('both'),
  isActive: z.boolean().default(true),
  sapMaterialCode: z.string().nullable().optional(),
  sapStorageLocationCode: z.string().nullable().optional(),
  locale: z.string().optional(),
  localizations: z.array(strapiLocalizationEntrySchema).optional(),
})

export const variantDtoSchema = variantSummaryDtoSchema.extend({
  description: z.string().nullable().optional(),
  images: z.array(z.any()).optional(),
  deliveryGroupingRule: deliveryGroupingRuleSchema.default('require_same'),
  sapDescription: z.string().nullable().optional(),
  sapBaseUnit: z.string().nullable().optional(),
  sapStorageLocationCode: z.string().nullable().optional(),
  sapLastChanged: z.string().nullable().optional(),
  sku: skuSummaryDtoSchema.optional(),
  minQuantity: z.number().nullable().optional(),
  maxQuantity: z.number().nullable().optional(),
  minOrderQuantity: z.number().nullable().optional(),
  maxOrderQuantity: z.number().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type VariantSummaryDto = z.infer<typeof variantSummaryDtoSchema>
export type VariantDto = z.infer<typeof variantDtoSchema>

// ============================================================================
// Request Schemas
// ============================================================================

export const createSKURequestSchema = z.object({
  name: localizedStringSchema,
  description: localizedStringSchema.optional(),
  isActive: z.boolean().default(true),
  categoryDocumentIds: z.array(z.string()).optional(),
})

export const updateSKURequestSchema = createSKURequestSchema.extend({
  documentId: z.string(),
})

export const createCategoryRequestSchema = z.object({
  name: localizedStringSchema,
  description: localizedStringSchema.optional(),
  code: z.string().optional(),
  color: z.string().optional(),
  parentDocumentId: z.string().optional(),
  isActive: z.boolean().default(true),
})

export const updateCategoryRequestSchema = createCategoryRequestSchema.extend({
  documentId: z.string(),
})

export type CreateCategoryRequest = z.infer<typeof createCategoryRequestSchema>
export type UpdateCategoryRequest = z.infer<typeof updateCategoryRequestSchema>

export const createVariantRequestSchema = z.object({
  skuDocumentId: z.string().min(1, 'SKU is required'),
  name: localizedStringSchema,
  description: localizedStringSchema.optional(),
  variantCode: z.string().min(1, 'Variant code is required'),
  barcode: z.string().min(1, 'Barcode is required'),
  weight: z.number().min(1, 'Weight must be at least 1'),
  packaging: packagingTypeSchema,
  portalAvailability: portalAvailabilitySchema.default('both'),
  deliveryGroupingRule: deliveryGroupingRuleSchema.default('require_same'),
  isActive: z.boolean().default(true),
  sapMaterialCode: z.string().max(50).optional(),
  sapStorageLocationCode: z.string().max(10).optional(),
  sapDescription: z.string().max(500).optional(),
  sapBaseUnit: z.string().max(10).optional(),
  imageIds: z.array(z.string()).optional(),
  minQuantity: z.number().nullable().optional(),
  maxQuantity: z.number().nullable().optional(),
  minOrderQuantity: z.number().nullable().optional(),
  maxOrderQuantity: z.number().nullable().optional(),
})

export const updateVariantRequestSchema = createVariantRequestSchema.extend({
  documentId: z.string(),
})

export type CreateSKURequest = z.infer<typeof createSKURequestSchema>
export type UpdateSKURequest = z.infer<typeof updateSKURequestSchema>
export type CreateVariantRequest = z.infer<typeof createVariantRequestSchema>
export type UpdateVariantRequest = z.infer<typeof updateVariantRequestSchema>

// ============================================================================
// Strapi Response Schemas
// ============================================================================

export const strapiMetaSchema = z.object({
  pagination: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      pageCount: z.number(),
      total: z.number(),
    })
    .optional(),
})

export const strapiListResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: z.array(dataSchema),
    meta: strapiMetaSchema,
  })

export const strapiSingleResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    data: dataSchema,
    meta: z.object({}).optional(),
  })

// ============================================================================
// API Endpoints
// ============================================================================

export const PRODUCTS_API_ENDPOINTS = {
  // SKUs
  LIST_SKUS: '/api/skus',
  GET_SKU: (documentId: string) => `/api/skus/${documentId}`,
  CREATE_SKU: '/api/skus',
  UPDATE_SKU: (documentId: string) => `/api/skus/${documentId}`,
  DELETE_SKU: (documentId: string) => `/api/skus/${documentId}`,

  // Variants
  LIST_VARIANTS: '/api/variants',
  GET_VARIANT: (documentId: string) => `/api/variants/${documentId}`,
  CREATE_VARIANT: '/api/variants',
  UPDATE_VARIANT: (documentId: string) => `/api/variants/${documentId}`,
  DELETE_VARIANT: (documentId: string) => `/api/variants/${documentId}`,

  // Categories
  LIST_CATEGORIES: '/api/categories',
  GET_CATEGORY: (documentId: string) => `/api/categories/${documentId}`,
  CREATE_CATEGORY: '/api/categories',
  UPDATE_CATEGORY: (documentId: string) => `/api/categories/${documentId}`,

  // Certificates
  LIST_CERTIFICATES: '/api/certificates',
  GET_CERTIFICATE: (documentId: string) => `/api/certificates/${documentId}`,
  CREATE_CERTIFICATE: '/api/certificates',
  UPDATE_CERTIFICATE: (documentId: string) => `/api/certificates/${documentId}`,
  DELETE_CERTIFICATE: (documentId: string) => `/api/certificates/${documentId}`,
} as const
